import { Button } from '@/components/ui/button';
import {
	Drawer,
	DrawerContent,
	DrawerFooter,
	DrawerTitle,
	DrawerDescription,
} from '@/components/ui/drawer';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ExternalLink, Gift, LogIn, Pen } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import useGetUser from '@/features/User/hooks/useGetUser';
import { useTranslation } from 'react-i18next';
import useGetPublicWishes from '../hooks/useGetPublicWishes';
import useGetPublicList from '@/features/List/hooks/useGetPublicList';
import useReserveWish from '../hooks/useReserveWish';
import { Sheet, SheetContent, SheetFooter } from '@/components/ui/sheet';

const WishSharedDetail = () => {
	const { t } = useTranslation();

	const location = useLocation();
	const navigate = useNavigate();
	const { public_uid, id_wish } = useParams();

	const { list } = useGetPublicList(public_uid || '');
	const { wishes } = useGetPublicWishes(public_uid || '');
	const { user } = useGetUser();

	const { reserveWish } = useReserveWish(public_uid as string);

	if (!public_uid) return null;

	const wish = wishes?.find(
		(wish) => wish.id === parseInt(id_wish as string)
	);

	const isOwner = list?.fk_user === user?.id;

	const wishWebsite = wish?.url ? new URL(wish?.url).hostname : '';
	const cleanWebsite = wishWebsite.replace('www.', '');

	const isMobile = window.innerWidth < 640;

	const renderMainAction = () => {
		if (!wish) return null;
		if (!user) {
			return (
				<Button
					variant="default"
					className="w-full"
					onClick={() => navigate('/?redirect=' + location.pathname)}
				>
					<LogIn className="mr-2 h-4 w-4" />
					{t('sharedList.detail.login.reserve')}
				</Button>
			);
		}
		if (isOwner) {
			return (
				<Button
					variant="default"
					className="w-full"
					onClick={() =>
						navigate(`/list/${list?.id}/edit/${id_wish}`)
					}
				>
					<Pen className="mr-2 h-4 w-4" /> {t('common.edit')}
				</Button>
			);
		}
		if (wish?.fk_user_reserved === user?.id) {
			return (
				<Button
					variant="default"
					className="w-full"
					onClick={() => reserveWish({ id_wish: wish.id })}
				>
					<Gift className="mr-2 h-4 w-4" />
					{t('wish.detail.unreserve')}
				</Button>
			);
		}
		if (wish?.fk_user_reserved) {
			return (
				<Button variant="default" className="w-full" disabled>
					<Gift className="mr-2 h-4 w-4" /> {t('wish.reserved')}
				</Button>
			);
		}
		return (
			<Button
				variant="default"
				className="w-full"
				onClick={() => reserveWish({ id_wish: wish.id })}
			>
				<Gift className="mr-2 h-4 w-4" />
				{t('wish.detail.reserve')}
			</Button>
		);
	};

	if (!isMobile) {
		return (
			<Sheet
				open={!!id_wish}
				onOpenChange={(open) =>
					!open && navigate(`/shared/${public_uid}`)
				}
			>
				<SheetContent className="p-0">
					<ScrollArea className="max-h-[calc(100%-50px)] overflow-auto p-5">
						{wish?.fk_user_reserved && (
							<div className="absolute right-6 top-6">
								<div className="self z-10 w-fit rounded-md bg-secondary px-2 py-1 text-xs font-medium text-secondary-foreground">
									{t('wish.reserved')}
								</div>
							</div>
						)}
						{wish?.image_key && (
							<img
								src={`${import.meta.env.VITE_API_URL}/image?imageKey=${wish?.image_key}`}
								alt={wish?.name}
								className="mb-4 max-h-[70vh] w-full rounded-md object-cover"
							/>
						)}
						<h1 className="text-lg font-bold">{wish?.name}</h1>
						{!!wish?.price && <p className="">{wish?.price}€</p>}
						{wish?.comment && (
							<p className="mt-4">{wish?.comment}</p>
						)}
						{wish?.url && (
							<Link
								className="mt-4 flex items-center underline"
								to={wish.url}
								target="_blank"
							>
								Voir sur {cleanWebsite}
								<ExternalLink className="ml-2 h-4 w-4" />
							</Link>
						)}
					</ScrollArea>
					<SheetFooter className="px-5">
						<Button
							className="w-full"
							variant="outline"
							onClick={() => navigate(`/shared/${public_uid}`)}
						>
							{t('common.back')}
						</Button>
						{renderMainAction()}
					</SheetFooter>
				</SheetContent>
			</Sheet>
		);
	}

	return (
		<Drawer
			open={!!id_wish}
			onOpenChange={(open) => !open && navigate(`/shared/${public_uid}`)}
		>
			<DrawerContent className="max-h-[100vh]">
				<DrawerTitle className="hidden">
					{wish?.name || 'Wish'}
				</DrawerTitle>
				<DrawerDescription className="hidden">
					Wish detail
				</DrawerDescription>
				<ScrollArea className="max-h-full overflow-auto p-5">
					{wish?.fk_user_reserved && (
						<div className="absolute right-6 top-6">
							<div className="self z-10 w-fit rounded-md bg-secondary px-2 py-1 text-xs font-medium text-secondary-foreground">
								{t('wish.reserved')}
							</div>
						</div>
					)}
					{wish?.image_key && (
						<img
							src={`${import.meta.env.VITE_API_URL}/image?imageKey=${wish?.image_key}`}
							alt={wish?.name}
							className="mb-4 max-h-[70vh] w-full rounded-md object-cover"
						/>
					)}
					<h1 className="text-lg font-bold">{wish?.name}</h1>
					{!!wish?.price && <p className="">{wish?.price}€</p>}
					{wish?.comment && <p className="mt-4">{wish?.comment}</p>}
					{wish?.url && (
						<Link
							className="mt-4 flex items-center underline"
							to={wish.url}
							target="_blank"
						>
							Voir sur {cleanWebsite}
							<ExternalLink className="ml-2 h-4 w-4" />
						</Link>
					)}
				</ScrollArea>
				<DrawerFooter>
					{renderMainAction()}
					<Button
						className="w-full"
						variant="outline"
						onClick={() => navigate(`/shared/${public_uid}`)}
					>
						{t('common.back')}
					</Button>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	);
};

export default WishSharedDetail;
